import React from 'react';
import type { PublicVisibleStatus } from '@find-my-part/types';
import { PublicQuestionList } from '../components/PublicQuestionList';
import { PublicQuestionSearch } from '../components/PublicQuestionSearch';
import { usePublicQuestions, useStyleInjection } from '../hooks';
import { BREAKPOINTS } from '../tokens';

const RECENT_SOLVED_LIMIT = 6;
const READY_CORPUS_THRESHOLD = 3;
const SOLVED_STATUS = ['RESOLVED'] as const satisfies readonly PublicVisibleStatus[];

export interface BrowseQuestionsViewProps {
  onAskQuestion: () => void;
  page?: number;
  onPageChange?: (page: number) => void;
}

export function BrowseQuestionsView({
  onAskQuestion,
  page = 1,
  onPageChange,
}: BrowseQuestionsViewProps): React.ReactElement {
  const discovery = usePublicQuestions({
    page,
    limit: RECENT_SOLVED_LIMIT,
    status: SOLVED_STATUS,
  });
  // The corpus gate is tenant-level, not a property of whichever ephemeral
  // browse page happens to be visible. Page 1 can reuse the display request;
  // direct or subsequent page visits make one bounded page-1 proof request so
  // search never flips between established and day-one states while paging.
  const readinessDiscovery = usePublicQuestions({
    page: 1,
    limit: RECENT_SOLVED_LIMIT,
    status: SOLVED_STATUS,
    enabled: page !== 1,
  });
  const corpusDiscovery = page === 1 ? discovery : readinessDiscovery;
  const answeredQuestions = discovery.questions.filter((question) => question.replyCount > 0);
  const corpusAnsweredQuestions = corpusDiscovery.questions.filter(
    (question) => question.replyCount > 0
  );
  const hasReadyCorpus =
    !corpusDiscovery.isLoading &&
    !corpusDiscovery.error &&
    (corpusDiscovery.pagination?.total ?? 0) >= READY_CORPUS_THRESHOLD &&
    corpusAnsweredQuestions.length >= READY_CORPUS_THRESHOLD;
  const disabledSearchMessage = corpusDiscovery.isLoading
    ? 'Checking which solved cases are available…'
    : corpusDiscovery.error
      ? 'Search is temporarily unavailable while solved cases are checked.'
      : undefined;
  const showCasesDuringReadinessFailure =
    page !== 1 && Boolean(corpusDiscovery.error) && !discovery.isLoading && !discovery.error;

  useStyleInjection(browseViewStyles, 'fmp-browse-view-styles');

  return (
    <div className="fmp-browse-view">
      <div className="fmp-browse-view__intro">
        <div className="fmp-browse-view__intro-copy">
          <h2 className="fmp-browse-view__title">Find the right part from real solved cases</h2>
          <p className="fmp-browse-view__lede">
            Search by part details, model numbers, markings, or the problem you are seeing.
          </p>
        </div>
        <button
          type="button"
          className="fmp-btn fmp-btn--primary fmp-browse-view__ask"
          onClick={onAskQuestion}
        >
          Ask with photos
        </button>
      </div>

      <PublicQuestionSearch
        enabled={hasReadyCorpus}
        onAskQuestion={onAskQuestion}
        disabledMessage={disabledSearchMessage}
        onRetryAvailability={
          page !== 1 && corpusDiscovery.error ? readinessDiscovery.refetch : undefined
        }
      />

      {discovery.isLoading && (
        <div className="fmp-browse-view__cases" aria-busy="true">
          <h3 className="fmp-browse-view__section-title">Recently solved cases</h3>
          <PublicQuestionList {...discovery} questions={[]} pagination={null} />
        </div>
      )}

      {!discovery.isLoading && discovery.error && (
        <div className="fmp-browse-view__cases">
          <h3 className="fmp-browse-view__section-title">Recently solved cases</h3>
          <PublicQuestionList {...discovery} questions={[]} pagination={null} />
        </div>
      )}

      {!discovery.isLoading &&
        !discovery.error &&
        !corpusDiscovery.isLoading &&
        !corpusDiscovery.error &&
        !hasReadyCorpus && (
          <div className="fmp-browse-view__starter" role="note">
            <h3 className="fmp-browse-view__section-title">Start with a few clear photos</h3>
            <p>
              Show the whole part, where it fits, and any model numbers or markings. Those details
              help the store identify a compatible replacement.
            </p>
            <ul>
              <li>Use bright, even light and keep the part in focus.</li>
              <li>Include a ruler or familiar object when size is hard to judge.</li>
              <li>Photograph labels, stamps, and the surrounding assembly.</li>
            </ul>
          </div>
        )}

      {!discovery.error && (hasReadyCorpus || showCasesDuringReadinessFailure) && (
        <div className="fmp-browse-view__cases">
          <h3 className="fmp-browse-view__section-title">Recently solved cases</h3>
          <PublicQuestionList
            {...discovery}
            questions={answeredQuestions}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
}

const browseViewStyles = `
  :where(.fmp-widget-scope) .fmp-browse-view {
    container-name: fmp-browse;
    container-type: inline-size;
    display: grid;
    gap: var(--fmp-space-section);
  }

  :where(.fmp-widget-scope) .fmp-browse-view__intro {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--fmp-space-xl);
  }

  :where(.fmp-widget-scope) .fmp-browse-view__intro-copy {
    max-width: var(--fmp-layout-prose-max);
  }

  :where(.fmp-widget-scope) .fmp-browse-view__title,
  :where(.fmp-widget-scope) .fmp-browse-view__section-title {
    margin: 0;
    color: var(--fmp-color-text);
    line-height: var(--fmp-line-height-heading);
  }

  :where(.fmp-widget-scope) .fmp-browse-view__title {
    font-size: var(--fmp-font-size-2xl);
  }

  :where(.fmp-widget-scope) .fmp-browse-view__section-title {
    font-size: var(--fmp-font-size-xl);
  }

  :where(.fmp-widget-scope) .fmp-browse-view__lede {
    margin: var(--fmp-space-sm) 0 0;
    color: var(--fmp-color-text-muted);
    line-height: var(--fmp-line-height-body);
  }

  :where(.fmp-widget-scope) .fmp-browse-view__ask {
    flex: 0 0 auto;
  }

  :where(.fmp-widget-scope) .fmp-browse-view .fmp-btn {
    min-height: var(--fmp-size-44);
    padding: var(--fmp-space-sm) var(--fmp-space-lg);
    border: var(--fmp-border-width) solid var(--fmp-color-primary);
    border-radius: var(--fmp-radius-control);
    font: inherit;
    font-weight: var(--fmp-font-weight-semibold);
    cursor: pointer;
  }

  :where(.fmp-widget-scope) .fmp-browse-view .fmp-btn--primary {
    background: var(--fmp-color-primary);
    color: var(--fmp-color-text-on-primary);
  }

  :where(.fmp-widget-scope) .fmp-browse-view .fmp-btn--secondary {
    background: var(--fmp-color-surface);
    color: var(--fmp-color-primary);
  }

  :where(.fmp-widget-scope) .fmp-browse-view .fmp-btn:hover:not(:disabled) {
    border-color: var(--fmp-color-primary-hover);
    background: var(--fmp-color-primary-hover);
    color: var(--fmp-color-text-on-primary);
  }

  :where(.fmp-widget-scope) .fmp-browse-view .fmp-btn:disabled {
    border-color: var(--fmp-color-border);
    background: var(--fmp-color-surface-muted);
    color: var(--fmp-color-text-muted);
    cursor: not-allowed;
  }

  :where(.fmp-widget-scope) .fmp-browse-view .fmp-btn:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-primary);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-browse-view .fmp-btn--primary:focus-visible {
    outline-color: var(--fmp-color-text-on-primary);
    outline-offset: var(--fmp-outline-inset-offset);
  }

  :where(.fmp-widget-scope) .fmp-browse-view__cases {
    display: grid;
    gap: var(--fmp-space-lg);
  }

  :where(.fmp-widget-scope) .fmp-browse-view__starter {
    display: grid;
    max-width: var(--fmp-layout-prose-max);
    justify-items: start;
    gap: var(--fmp-space-md);
    padding: var(--fmp-space-xl);
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-surface);
    background: var(--fmp-color-surface-muted);
    color: var(--fmp-color-text-secondary);
    line-height: var(--fmp-line-height-body);
  }

  :where(.fmp-widget-scope) .fmp-browse-view__starter p,
  :where(.fmp-widget-scope) .fmp-browse-view__starter ul {
    margin: 0;
  }

  :where(.fmp-widget-scope) .fmp-browse-view__starter ul {
    padding-inline-start: var(--fmp-space-xl);
  }

  @container fmp-browse (width < ${BREAKPOINTS.container.compact}) {
    :where(.fmp-widget-scope) .fmp-browse-view__intro {
      flex-direction: column;
    }
  }
`;
